import { useMemo, useState } from 'react';
import { GitMerge } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  StatusPill,
  Pagination,
  Stat,
  StatGroup,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { paginationView } from '../pagination-summary';
import { Button, Card, CardBody } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  useCustomerInvoices,
  useCustomerInvoiceStatistical,
  useMergeInvoices,
  type InvoiceRow,
  type InvoiceStatisticalRow,
} from '@/api/customer-invoices';
import { formatDate, formatMoney } from '@/lib/format';
import { appliedRange } from './applied-range';
import { invoiceTone, humanize } from './status-tones';
import { TabError, TabLoading } from './tab-states';

/** Extract a human-readable message from an axios/unknown error. */
function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? 'Something went wrong.';
}

/** Only waiting-confirm and approved invoices are eligible for merge. */
function isMergeable(row: InvoiceRow): boolean {
  return row.invoice_status === 'waiting-confirm' || row.invoice_status === 'approved';
}

const PAGE_SIZE = 20;

/** A service reference embedded in an invoice line item. */
interface InvoiceLineService {
  service_id?: number;
  service_name?: string;
}

/** Sum `total_amount` across statistical rows whose status is in `statuses`. */
function sumByStatus(rows: InvoiceStatisticalRow[], statuses: string[]): number {
  return rows
    .filter((r) => statuses.includes(r.invoice_status))
    .reduce((acc, r) => acc + (r.total_amount || 0), 0);
}

/** Unique services rendered from an invoice's line items (or "Deposit"). */
function invoiceServices(row: InvoiceRow) {
  if (row.invoice_type === 'deposit') return 'Deposit';
  const items = (row.invoice_items ?? []) as { service?: InvoiceLineService }[];
  const seen = new Map<number, InvoiceLineService>();
  for (const item of items) {
    const svc = item?.service;
    if (svc?.service_id != null && !seen.has(svc.service_id)) seen.set(svc.service_id, svc);
  }
  const services = Array.from(seen.values());
  if (services.length === 0) return '-';
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
      {services.map((svc) => (
        <EntityLink
          key={svc.service_id}
          to={`/services/${svc.service_id}`}
          id={svc.service_id!}
          name={svc.service_name}
        />
      ))}
    </div>
  );
}

/**
 * Invoices tab — 4 statistical Stat cards (Total / Paid / Unpaid / Cancelled)
 * on top of the server-paginated invoices DataTable with Merge Invoices bulk action.
 * Only waiting-confirm/approved rows are selectable for merge (USER runs the mutation).
 */
export function InvoicesTab({ id }: { id: number }) {
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [mergeOpen, setMergeOpen] = useState(false);
  const stats = useCustomerInvoiceStatistical(id);
  const list = useCustomerInvoices(id, { page, page_size: PAGE_SIZE });
  const merge = useMergeInvoices(id);

  const statRows = stats.data?.results ?? [];
  const cards = useMemo(() => {
    const total = statRows.reduce((acc, r) => acc + (r.total_amount || 0), 0);
    return {
      total,
      paid: sumByStatus(statRows, ['paid', 'done']),
      unpaid: sumByStatus(statRows, ['waiting-confirm', 'approved']),
      cancelled: sumByStatus(statRows, ['cancelled', 'rejected']),
    };
  }, [statRows]);

  const rows = list.data?.results ?? [];
  const total = list.data?.pagination?.total ?? rows.length;
  const pageCount = list.data?.pagination?.total_pages ?? 1;

  // Only mergeable (waiting-confirm / approved) selected rows count toward the bulk action.
  const selectedIds = rows
    .filter((r) => selection[String(r.invoice_id)] && isMergeable(r))
    .map((r) => r.invoice_id);
  const hasSelection = selectedIds.length >= 2;

  const submitMerge = () => {
    merge.mutate(selectedIds, {
      onSuccess: () => {
        setMergeOpen(false);
        setSelection({});
        merge.reset();
      },
    });
  };

  const columns = useMemo<ColumnDef<InvoiceRow, unknown>[]>(
    () => [
      {
        id: 'invoice_id',
        header: 'Invoice ID',
        cell: ({ row }) => (
          <TableLink to={`/invoices/${row.original.invoice_id}`}>
            {row.original.invoice_id}
          </TableLink>
        ),
      },
      { id: 'invoice_services', header: 'Services', cell: ({ row }) => invoiceServices(row.original) },
      {
        id: 'invoice_total',
        header: () => <div style={{ textAlign: 'right' }}>Total</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.invoice_total)}</div>
        ),
      },
      {
        id: 'invoice_applied',
        header: 'Applied',
        cell: ({ row }) =>
          appliedRange(row.original.invoice_apply_startdate, row.original.invoice_apply_enddate),
      },
      {
        id: 'invoice_status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={invoiceTone(row.original.invoice_status)}>
            {humanize(row.original.invoice_status)}
          </StatusPill>
        ),
      },
      { id: 'invoice_duedate', header: 'Due', cell: ({ row }) => formatDate(row.original.invoice_duedate) },
      { id: 'created_at', header: 'Created', cell: ({ row }) => formatDate(row.original.created_at) },
      {
        id: 'invoice_payment_at',
        header: 'Payment At',
        cell: ({ row }) => formatDate(row.original.invoice_payment_at),
      },
      {
        id: 'invoice_payment_method',
        header: 'Payment Method',
        cell: ({ row }) => humanize(row.original.invoice_payment_method),
      },
    ],
    [],
  );

  if (list.isError) return <TabError title="Failed to load invoices" onRetry={() => list.refetch()} />;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardBody>
          <StatGroup columns={4}>
            <Stat label="Invoiced" dotColor="var(--vn-primary-solid)" value={formatMoney(cards.total)} />
            <Stat label="Paid" dotColor="var(--vn-success-solid)" value={formatMoney(cards.paid)} />
            <Stat label="Outstanding" dotColor="var(--vn-warning-solid)" value={formatMoney(cards.unpaid)} />
            <Stat label="Cancelled" dotColor="var(--vn-danger-solid)" value={formatMoney(cards.cancelled)} />
          </StatGroup>
        </CardBody>
      </Card>

      {/* Bulk action toolbar — shown when ≥2 mergeable rows are selected */}
      {hasSelection ? (
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              {selectedIds.length} invoice{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
          }
          end={
            <Button
              variant="secondary"
              startIcon={<GitMerge size={15} />}
              onClick={() => setMergeOpen(true)}
            >
              Merge Invoices
            </Button>
          }
        />
      ) : null}

      {list.isLoading ? (
        <TabLoading />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          enableSorting={false}
          enableSelection
          rowSelection={selection}
          onRowSelectionChange={setSelection}
          getRowId={(r) => String(r.invoice_id)}
          emptyState={<EmptyState title="No invoices" description="This customer has no invoices yet." />}
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination page={page} pageCount={view.pageCount} onPageChange={setPage} summary={view.summary} />
            );
          })()}
        />
      )}

      <ConfirmDialog
        open={mergeOpen}
        onOpenChange={(o) => { if (!merge.isPending) { setMergeOpen(o); if (!o) merge.reset(); } }}
        title="Merge Invoices"
        description={`Merge ${selectedIds.length} selected invoices into one? This action cannot be undone.`}
        confirmLabel="Merge"
        loading={merge.isPending}
        onConfirm={submitMerge}
      >
        {merge.isError ? (
          <Alert tone="danger" title="Could not merge invoices">{errMsg(merge.error)}</Alert>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
